import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v4';
import {
  LifecycleStatus,
  PictureInitialParentType,
} from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { PictureDropzone } from '~/components/upload/supporting-object/picture-dropzone';
import { useRefetchQueriesV4 } from '~/hooks/apollo';
import type { RefetchQueries } from '~/utils/graphql';
import { uploadFile } from '~/utils/modules/file';
import { fileSizeText } from '~/utils/text';

const CREATE_OUTCROP_THUMBNAIL = graphql(`
  mutation CreateOutcropThumbnail($input: PictureCreateInput!) {
    pictureCreate(input: $input) {
      token
      path
    }
  }
`);

export function OutcropThumbnailUploader({
  outcropId,
  outcropName,
  refetchQueries,
}: {
  outcropId: number;
  outcropName: string;
  refetchQueries: RefetchQueries;
}) {
  const [createThumbnail, { loading }] = useMutation(CREATE_OUTCROP_THUMBNAIL);
  const [refetch] = useRefetchQueriesV4(refetchQueries);

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  function handleDrop(files: File[]) {
    setFile(files[0]);
  }

  async function upload() {
    if (!file) {
      console.log('No file selected to upload.');
      return;
    }
    setIsUploading(true);

    try {
      const res = await createThumbnail({
        variables: {
          input: {
            picture: {
              parentType: PictureInitialParentType.OutcropThumbnail,
              parentId: outcropId,
              name: outcropName,
              description: `${outcropName} Thumbnail`,
              author: null,
              type: 'thumbnail',
              lifecycleStatus: LifecycleStatus.Published,
            },
          },
        },
      });
      invariant(res.data?.pictureCreate, 'Missing upload path or token');
      const { path, token } = res.data.pictureCreate;

      const uploadResult = await uploadFile({ path, token, file });
      if (!uploadResult) {
        throw new Error('POST file failed');
      }
      await refetch();
      toast.success('Thumbnail created successfully.');
    } catch (err) {
      console.log('Error uploading file', err);
      toast.error('Error creating thumbnail.');
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="space-y-4">
      <PictureDropzone hasFiles={!!file} onDrop={handleDrop} multiple={false} />
      {file && (
        <div className="card shadow">
          <div className="card-body">
            <div className="flex gap-4 items-start">
              <div
                className="w-24 h-24 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${URL.createObjectURL(file)})`,
                }}
              />
              <div className="flex-1 space-y-4">
                <div>
                  <div className="text-lg font-semibold">{file.name}</div>
                  <div className="text-sm text-slate-500">
                    {fileSizeText(file.size)}
                  </div>
                </div>

                <Button
                  type="button"
                  color="primary"
                  onClick={upload}
                  disabled={!file}
                  loading={loading || isUploading}
                >
                  Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
